import { parseColorToRGBA } from '@wix/thunderbolt-components'
import { color as commonColor, MAX_Z_INDEX, PORTALS_Z_INDEX } from '@wix/thunderbolt-commons'
import type { ThemeFontsAndColorsCssData } from './themeFontsAndColors.types'
import { DEFAULT_DESKTOP_SITE_WIDTH, MOBILE_SITE_WIDTH } from '../../utils/constants'
import type { CssObject, SelectorObj } from '../../shared.types'
import { FALLBACK_MAXIMUM_VIEWPORT, FALLBACK_MINIMUM_VIEWPORT } from '../styleBased/styleBasedDomApplier'
import { resolveThemeFontsSpx, getScaledFontSize } from '@wix/thunderbolt-becky-root'

const { getSplitRgbValuesString } = commonColor

const getThemeColorsVars = (deps: ThemeFontsAndColorsCssData) => {
	const themeColorsVars = deps?.colorData?.reduce((result: CssObject, color: string, index: number) => {
		const key = `--color_${index}`
		const rgbValues = getSplitRgbValuesString(color)
		result[key] = rgbValues ? rgbValues : ''
		return result
	}, {})

	return themeColorsVars || {}
}

const getThemeFontsVars = (deps: ThemeFontsAndColorsCssData) => {
	if (deps!.fontData) {
		const themeFontsSpxResolved = resolveThemeFontsSpx(deps!.fontData)

		const themeFontsVars = Object.fromEntries(
			Object.entries(themeFontsSpxResolved).map(([fontKey, fontValue]) => ['--' + fontKey, fontValue])
		)

		return themeFontsVars
	}
	return {}
}

const getFreemiumBannerHeight = (deps: ThemeFontsAndColorsCssData) => {
	const isStudio = deps?.editorName === 'Studio' || !!deps?.experiments['specs.thunderbolt.forceStudio']
	if (deps?.isWixAdsAllowed && deps?.shouldRenderWixAdsOnNavigation) {
		if (isStudio && !deps.shouldShowCoBrandingBanner) {
			return 30
		} else if (deps.isMobileView) {
			return 38
		}

		return 50
	}
	return 0
}

const getGlobalSiteCssVars = (deps: ThemeFontsAndColorsCssData) => {
	const freemiumBannerHeight = getFreemiumBannerHeight(deps)
	const siteWidth = (deps.isMobileView && MOBILE_SITE_WIDTH) || deps.siteWidth || DEFAULT_DESKTOP_SITE_WIDTH

	return {
		'--wix-ads-height': `${freemiumBannerHeight}px`,
		'--sticky-offset': `${freemiumBannerHeight}px`,
		'--wix-ads-top-height': `${freemiumBannerHeight}px`,
		'--site-width': `${siteWidth}px`,
		'--above-all-z-index': `${MAX_Z_INDEX}`,
		'--portals-z-index': `${PORTALS_Z_INDEX}`,
		'--wix-opt-in-direction': deps.directionWithOptInRtlSupport,
	}
}

const getFontSmoothingStyles = () => {
	return {
		'-webkit-font-smoothing': 'antialiased',
		'-moz-osx-font-smoothing': 'grayscale',
	}
}

const getCssVarsForDefaultBreakpoint = () => {
	return {
		'--minViewportSize': FALLBACK_MINIMUM_VIEWPORT,
		'--maxViewportSize': FALLBACK_MAXIMUM_VIEWPORT,
	}
}

const getThemeSpxRatio = (deps: ThemeFontsAndColorsCssData) => {
	const refWidth = deps?.themeData?.spx?.refWidth ?? 1

	const themeRatio = deps?.themeData?.spx?.resolverType === 'scale' ? `var(--scaling-factor) / ${refWidth}` : '1px'

	return {
		'--theme-spx-ratio': `${themeRatio}`,
	}
}

export const getThemeFontsClasses = (deps: ThemeFontsAndColorsCssData) => {
	const themeFontsClasses = deps!.fontData?.reduce((result: SelectorObj, _font, index: number) => {
		const fontColor =
			deps!.fontColorMap && deps!.fontColorMap[`font_${index}`] ? deps!.fontColorMap[`font_${index}`] : undefined
		const color = parseColorToRGBA(fontColor, 1)
		const textTheme = deps!.textTheme?.[index]
		const letterSpacingValue = textTheme ? textTheme.letterSpacing : null
		const letterSpacing: { string: string } | {} = letterSpacingValue
			? { 'letter-spacing': letterSpacingValue }
			: {}
		let fontSize: string | undefined
		if (textTheme?.scale_minFontSize && textTheme?.scale_maxFontSize) {
			fontSize = getScaledFontSize(textTheme.scale_minFontSize, textTheme.scale_maxFontSize)
		} else if (textTheme?.fontSize.endsWith('spx') && (textTheme?.minFontSize || textTheme?.maxFontSize)) {
			const hasMinFontSize = !!textTheme?.minFontSize
			const hasMaxFontSize = !!textTheme?.maxFontSize
			const hasMinAndMaxFontSize = hasMinFontSize && hasMaxFontSize
			const sizeValue = `calc(${parseInt(textTheme.fontSize, 10)} * var(--theme-spx-ratio))`
			const fontSizeWithMinOnly = `max(${textTheme.minFontSize}, ${sizeValue})`
			const fontSizeWithMaxOnly = `min(${textTheme.maxFontSize}, ${sizeValue})`
			const fontSizeWithMinAndMax = `max(${textTheme.minFontSize}, min(${textTheme.maxFontSize}, ${sizeValue}))`
			const fontSizeWithoutMin = hasMaxFontSize ? fontSizeWithMaxOnly : sizeValue
			fontSize = hasMinAndMaxFontSize
				? fontSizeWithMinAndMax
				: hasMinFontSize
				? fontSizeWithMinOnly
				: fontSizeWithoutMin
		}
		result[`.font_${index}`] = {
			font: `var(--font_${index})`,
			color,
			...letterSpacing,
			...(fontSize && { 'font-size': fontSize }),
		}
		return result
	}, {})

	return themeFontsClasses || {}
}

export const getThemeColorsClasses = (deps: ThemeFontsAndColorsCssData) => {
	const themeColorsClasses = deps?.colorData?.reduce((result: SelectorObj, _, index: number) => {
		result[`.color_${index}`] = { color: `rgb(var(--color_${index}))` }
		return result
	}, {})

	return themeColorsClasses || {}
}

export const getThemeBackColorsClasses = (deps: ThemeFontsAndColorsCssData) => {
	const themeBackColorsClasses = deps?.colorData?.reduce((result: SelectorObj, _, index: number) => {
		result[`.backcolor_${index}`] = { 'background-color': `rgb(var(--color_${index}))` }
		return result
	}, {})

	return themeBackColorsClasses || {}
}

export const getRootCssVars = (deps: ThemeFontsAndColorsCssData) => {
	const themeColorsVars = getThemeColorsVars(deps)
	const themeFontsVars = getThemeFontsVars(deps)
	const globalSiteCssVars = getGlobalSiteCssVars(deps)
	const fontSmoothingStyles = getFontSmoothingStyles()
	const cssVarsForDefaultBreakpoint = getCssVarsForDefaultBreakpoint()
	const themeRatio = getThemeSpxRatio(deps)

	// @ts-ignore
	const isSpxStopperExperimentOn = globalThis.spxStopperExperiment || false

	return {
		':root,:host, .spxThemeOverride, .max-width-container': {
			...themeColorsVars,
			...themeFontsVars,
			...globalSiteCssVars,
			...fontSmoothingStyles,
			...cssVarsForDefaultBreakpoint,
			...themeRatio,
			...{
				'--scaling-factor': isSpxStopperExperimentOn
					? 'clamp(var(--spx-stopper-min), (100 * var(--one-unit)), min(var(--spx-stopper-max), var(--section-max-width)))'
					: `min((100 * var(--one-unit)), var(--section-max-width))`,
			},
			...{
				'--customScaleViewportLimit':
					'clamp(var(--minViewportSize) * 1px, 100 * var(--one-unit), min(var(--section-max-width), var(--maxViewportSize) * 1px))',
			},
		},
	}
}
